import React, { useState } from 'react';
import { Box, Tabs, Tab, Typography, Stack } from '@mui/material';
import {
  InfoOutlined,
  PersonOutlined,
  InsightsOutlined,
  CheckCircleOutlined,
  ArrowRightAlt,
  LocationCityOutlined,
  ElectricBoltOutlined,
  AnalyticsOutlined,
  CalendarMonthOutlined,
  BusinessOutlined,
  EngineeringOutlined,
  MiscellaneousServicesOutlined,
  ElectricalServicesOutlined,
  SchoolOutlined,
  DataUsageOutlined,
  TrendingUpOutlined,
  SmartToyOutlined,
  LightbulbOutlined,
  DevicesOutlined,
  ApartmentOutlined
} from '@mui/icons-material';

// Custom styled list item component with icon
const FancyListItem = ({ icon, children }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      mb: 1.5,
      transition: 'transform 0.2s ease-in-out',
      '&:hover': {
        transform: 'translateX(5px)',
      }
    }}
  >
    <Box
      sx={{
        mr: 1.5,
        color: 'primary.main',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {icon}
    </Box>
    <Typography
      variant="body1"
      sx={{
        pt: 0.25,
        fontWeight: 400
      }}
    >
      {children}
    </Typography>
  </Box>
);

const FramedTabs = () => {
  const [tabIndex, setTabIndex] = useState(0);

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const featuresContent = (
    <Stack spacing={1}>
      <Typography component="p" sx={{ mb: 2 }}>
        The Flexibility Assessment Tool is a platform designed within the EU-funded project DEDALUS. It helps energy stakeholders across seven EU countries to analyze and manage energy flexibility in buildings or apartments.
      </Typography>
      <Typography component="h6" sx={{ mt: 1, mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
        Key Features
      </Typography>
      <FancyListItem icon={<LocationCityOutlined color="primary" />}>
        Supports stakeholders from Greece, Spain, Italy, Denmark, Austria, Romania, and Ireland
      </FancyListItem>
      <FancyListItem icon={<ElectricBoltOutlined color="primary" />}>
        Enables energy flexibility assessment and forecasting
      </FancyListItem>
      <FancyListItem icon={<AnalyticsOutlined color="primary" />}>
        Integrates Non-Intrusive Load Monitoring (NILM) for detailed energy insights
      </FancyListItem>
      <FancyListItem icon={<CalendarMonthOutlined color="primary" />}>
        Provides detailed suggestions for energy management up to 7 days in advance
      </FancyListItem>
    </Stack>
  );

  const usersContent = (
    <Stack spacing={1}>
      <Typography component="p" sx={{ mb: 3 }}>
        The main users of the Tool are:
      </Typography>
      <FancyListItem icon={<BusinessOutlined color="primary" />}>
        Building Managers
      </FancyListItem>
      <FancyListItem icon={<EngineeringOutlined color="primary" />}>
        Facility Operators
      </FancyListItem>
      <FancyListItem icon={<MiscellaneousServicesOutlined color="primary" />}>
        ESCOs
      </FancyListItem>
      <FancyListItem icon={<ElectricalServicesOutlined color="primary" />}>
        Utility Companies
      </FancyListItem>
      <FancyListItem icon={<SchoolOutlined color="primary" />}>
        Researchers & Academics
      </FancyListItem>
    </Stack>
  );

  const insightsContent = (
    <Stack spacing={1}>
      <Typography component="p" sx={{ mb: 2 }}>
        The platform provides comprehensive analytics and AI-powered insights to help users optimize their energy consumption and flexibility.
      </Typography>
      <Typography component="h6" sx={{ mt: 1, mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
        Analytics & Insights
      </Typography>
      <FancyListItem icon={<DataUsageOutlined color="primary" />}>
        Comprehensive data analytics from buildings, apartments, and energy sources
      </FancyListItem>
      <FancyListItem icon={<TrendingUpOutlined color="primary" />}>
        Detailed usage patterns including average, minimum, and maximum consumption analysis
      </FancyListItem>
      <FancyListItem icon={<ElectricBoltOutlined color="primary" />}>
        Flexibility indicators derived from historical energy data
      </FancyListItem>
      <FancyListItem icon={<DevicesOutlined color="primary" />}>
        Device-level analysis, even when specific device data is unavailable
      </FancyListItem>
      <FancyListItem icon={<ApartmentOutlined color="primary" />}>
        Building and apartment-level energy assessment capabilities
      </FancyListItem>
      <FancyListItem icon={<SmartToyOutlined color="primary" />}>
        AI and Machine Learning models for advanced energy optimization
      </FancyListItem>
      <FancyListItem icon={<LightbulbOutlined color="primary" />}>
        Actionable recommendations and optimization measures up to 7 days in advance
      </FancyListItem>
    </Stack>
  );

  return (
    <Box
      sx={{
        border: '2px solid #ccc',
        borderRadius: '8px',
        p: 3,
        backgroundColor: '#fff', // Background for the framed tab section
        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
      }}
    >
      {/* Tabs */}
      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        centered
        variant="fullWidth"
        sx={{ mb: 3 }}
      >
        <Tab
          icon={<InfoOutlined />}
          iconPosition="start"
          label="What it is"
        />
        <Tab
          icon={<PersonOutlined />}
          iconPosition="start"
          label="For Whom"
        />
        <Tab
          icon={<InsightsOutlined />}
          iconPosition="start"
          label="Insights"
        />
      </Tabs>

      {/* Tab Content */}
      <Box>
        {tabIndex === 0 && featuresContent}
        {tabIndex === 1 && usersContent}
        {tabIndex === 2 && insightsContent}
      </Box>
    </Box>
  );
};

export default FramedTabs;